import React from 'react';
import Modal, { ModalProps } from 'react-bootstrap/Modal';
import useModalAriaHidden from '@/hooks/useModalAriaHidden';

interface AccessibleModalProps extends ModalProps {
  // Custom selector for the root element (defaults to '#root')
  rootElementSelector?: string;
}

/**
 * Higher-Order Component that enhances React Bootstrap Modal with automatic
 * aria-hidden management on the app's root element for improved accessibility.
 *
 * This component ensures that when the modal is open, the background content
 * is hidden from screen readers and focus is trapped within the modal.
 *
 * @example
 * // Basic usage - same as react-bootstrap Modal but with accessibility features
 * <AccessibleModal show={showModal} onHide={handleClose}>
 *   <AccessibleModal.Header closeButton>
 *     <AccessibleModal.Title>My Modal</AccessibleModal.Title>
 *   </AccessibleModal.Header>
 *   <AccessibleModal.Body>
 *     Modal content here
 *   </AccessibleModal.Body>
 * </AccessibleModal>
 *
 * @example
 * // With custom root selector
 * <AccessibleModal
 *   show={showModal}
 *   onHide={handleClose}
 *   rootElementSelector="#app-root"
 * >
 *   Modal content...
 * </AccessibleModal>
 */
const createAccessibleModal = () => {
  const AccessibleModal: React.FC<AccessibleModalProps> = ({ show = false, rootElementSelector, ...modalProps }) => {
    useModalAriaHidden(show, rootElementSelector);
    return <Modal show={show} {...modalProps} />;
  };

  // Copy all static properties from the original Modal
  Object.setPrototypeOf(AccessibleModal, Modal);
  Object.assign(AccessibleModal, Modal);

  return AccessibleModal as typeof Modal & React.FC<AccessibleModalProps>;
};

const AccessibleModal = createAccessibleModal();

export default AccessibleModal;
